import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const round = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Split a consumption/wastage quantity (in sqft) across one or more roll serials.
// value: [{ serial_id, quantity }]. Emits the same shape via onChange.
export const RollAllocator = ({ studioId, category, skuId, totalQty = 0, value = [], onChange, testidPrefix = "roll" }) => {
  const [serials, setSerials] = useState([]);
  const rows = value.length ? value : [{ serial_id: "", quantity: "" }];

  useEffect(() => {
    if (!studioId || !category) return;
    const skuQ = skuId ? `&for_sku_id=${skuId}` : "";
    api
      .get(`/roll-serials?usable=true&has_balance=true&studio_id=${studioId}&category=${category}${skuQ}`)
      .then(({ data }) => setSerials(data || []))
      .catch(() => setSerials([]));
  }, [studioId, category, skuId]);

  const balanceOf = (id) => Number((serials.find((s) => s.id === id) || {}).balance_sqft || 0);
  const allocated = round(rows.reduce((a, r) => a + (Number(r.quantity) || 0), 0));
  const remaining = round(totalQty - allocated);
  const usedIds = rows.map((r) => r.serial_id).filter(Boolean);

  const update = (next) => onChange(next.filter((r) => r.serial_id));

  const setRow = (i, patch) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange(next.filter((r) => r.serial_id));
  };

  const pickSerial = (i, serialId) => {
    const bal = balanceOf(serialId);
    const need = round(totalQty - rows.reduce((a, r, idx) => a + (idx === i ? 0 : Number(r.quantity) || 0), 0));
    const suggested = need > 0 ? Math.min(bal, need) : bal;
    setRow(i, { serial_id: serialId, quantity: suggested > 0 ? String(round(suggested)) : "" });
  };

  const addRow = () => onChange([...rows.filter((r) => r.serial_id), { serial_id: "", quantity: "" }]);
  const removeRow = (i) => update(rows.filter((_, idx) => idx !== i));

  const hasExisting = rows.some((r) => r.serial_id);
  if (studioId && category && !serials.length && !hasExisting) {
    return (
      <div
        className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
        data-testid={`${testidPrefix}-empty`}
      >
        <p className="font-semibold">No {category} roll serials in stock at this studio.</p>
        <p className="mt-1 leading-relaxed">
          PPF / Sunfilm consumption must be traced to a physical roll, so a serial number is
          required. Ask HQ to dispatch serialised rolls to this studio, or an Admin / Accountant can
          add them on the <span className="font-medium">Stock page → Opening stock</span> (a serial is
          generated automatically). Seeing the product in this list only means it exists in the HQ
          catalogue — it does not mean serialised rolls have been received here yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-brand-line bg-slate-50/60 p-2.5" data-testid={`${testidPrefix}-allocator`}>
      {rows.map((r, i) => {
        const options = serials.filter((s) => s.id === r.serial_id || !usedIds.includes(s.id));
        const over = Number(r.quantity) > balanceOf(r.serial_id) + 0.001;
        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Select value={r.serial_id || ""} onValueChange={(v) => pickSerial(i, v)}>
              <SelectTrigger className="min-h-[40px] w-full min-w-0 flex-1 bg-white sm:w-auto sm:min-w-[200px]" data-testid={`${testidPrefix}-serial-${i}`}>
                <SelectValue placeholder={serials.length ? "Select roll serial" : "No rolls in stock"} />
              </SelectTrigger>
              <SelectContent>
                {options.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`${testidPrefix}-opt-${s.serial}`}>
                    {s.serial}{s.variant ? ` · ${s.variant}` : ""} — {Math.round(s.balance_sqft)} sqft left
                    {s.status === "dispatched" ? " · in transit" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              className={`w-28 bg-white ${over ? "border-rose-400" : ""}`}
              inputMode="decimal"
              placeholder="sqft"
              data-testid={`${testidPrefix}-qty-${i}`}
              value={r.quantity ?? ""}
              onChange={(e) => setRow(i, { quantity: e.target.value })}
            />
            {r.serial_id && (
              <span className={`text-xs ${over ? "text-rose-600" : "text-slate-400"}`}>
                / {Math.round(balanceOf(r.serial_id))} left
              </span>
            )}
            {rows.length > 1 && (
              <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" data-testid={`${testidPrefix}-remove-${i}`} onClick={() => removeRow(i)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        );
      })}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button size="sm" variant="outline" className="h-8" data-testid={`${testidPrefix}-add`} onClick={addRow}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Add roll
        </Button>
        {totalQty > 0 && (
          <span
            data-testid={`${testidPrefix}-summary`}
            className={`text-xs font-medium ${
              Math.abs(remaining) < 0.01 ? "text-emerald-600" : remaining > 0 ? "text-amber-600" : "text-rose-600"
            }`}
          >
            Allocated {allocated} / {round(totalQty)} sqft
            {Math.abs(remaining) >= 0.01 && (remaining > 0 ? ` · ${remaining} left to cover` : ` · ${-remaining} over`)}
          </span>
        )}
      </div>
    </div>
  );
};
